import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  Paper,
  Snackbar,
  Alert
} from "@mui/material";

import Header from "./components/Header";
import CompanyCard from "./components/CompanyCard";
import SearchBar from "./components/SearchBar";
import Filters from "./components/Filters";
import SortBar from "./components/SortBar";
import ResetFilters from "./components/ResetFilters";
import Overview from "./components/Overview";
import TabMiniCharts from "./components/TabMiniCharts";
import Login from "./components/Login";

import exportFeedbackToCSV from "./helpers/exportFeedbackToCSV.js";

const BUCKET_MAP = {
  biotechnology: "Biotech",
  "life science": "Biotech",
  oncology: "Biotech",
  therapeutics: "Biotech",

  pharmaceutical: "Pharmaceutical",

  "health care": "Healthcare",
  medical: "Healthcare",

  "artificial intelligence": "Artificial Intelligence",
  "artificial intelligence (ai)": "Artificial Intelligence",

  agtech: "AgTech",
  agriculture: "AgTech",
  "animal feed": "AgTech",
  "animal health": "AgTech"
};

function normalizeBuckets(industries) {
  if (!industries) return [];
  const list = Array.isArray(industries)
    ? industries
    : typeof industries === "string"
    ? industries.split(",")
    : [];

  const cleaned = list
    .filter((x) => typeof x === "string")
    .map((x) => x.trim().toLowerCase());

  const buckets = new Set();
  cleaned.forEach((x) => {
    if (BUCKET_MAP[x]) buckets.add(BUCKET_MAP[x]);
  });

  return Array.from(buckets);
}

function normalizeCountry(location) {
  if (!location || typeof location !== "string") return "Unknown";
  const parts = location.split(",").map((p) => p.trim());
  return parts[parts.length - 1] || "Unknown";
}

function mapFundingStage(type) {
  if (!type) return "Unknown";
  const t = type.toLowerCase();

  if (t.includes("pre-seed") || t.includes("pre seed")) return "Pre-Seed";
  if (t.includes("seed")) return "Seed";
  if (t.includes("series a")) return "Series A";
  if (t.includes("series b")) return "Series B";
  if (t.includes("series c")) return "Series C";
  if (t.includes("grant")) return "Grant";

  return type || "Unknown";
}

function getFundingMonth(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-GB", { month: "long" });
}

function getFundingQuarter(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return `Q${Math.floor(date.getMonth() / 3) + 1}`;
}

function getFundingYear(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return String(date.getFullYear());
}

function getCompanyKey(company) {
  return company.organizationNameUrl || company.organizationName || "";
}

const SAVED_SEARCHES_KEY = "lead_saved_searches";
const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];

function normalizeSelection(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim() !== "") return [value];
  return [];
}

function normalizeCompany(c, isNewAddition = false) {
  const companyId = c.organizationNameUrl;
  const classification = c.state || c["Scope classification"] || "";
  const reasoning = c.stateJustification || c.Justification;
  const leadType =
    classification === "INTEREST"
      ? "ACCEPT"
      : classification === "NOT_INTEREST"
      ? "NOT ACCEPT"
      : "FUZZY";

  const rankString = c.cbRankCompany || c.cbRankOrganization || "";
  const rankNumber =
    typeof rankString === "string"
      ? parseInt(rankString.replace(/,/g, ""), 10)
      : null;
  const versionNumber =
    typeof c.version === "number"
      ? c.version
      : parseInt(String(c.version ?? "0"), 10) || 0;
  const isUpdatedCompany = !isNewAddition && versionNumber >= 1;
  const updateCategory = isNewAddition
    ? "New Company"
    : isUpdatedCompany
    ? "Updates"
    : "";

  return {
    id: companyId,
    "Organization Name": c.organizationName || "Unknown",
    Description: c.description || c.fullDescription || "Unknown",

    Industries: c.industries || [],
    BucketIndustries: normalizeBuckets(c.industries),

    Website: c.website || "",
    Crunchbase: c.organizationNameUrl || "",

    Founded: c.foundedDate || null,
    "Last Funding Date": c.lastFundingDate || null,
    "Funding Month": getFundingMonth(c.lastFundingDate),
    "Funding Quarter": getFundingQuarter(c.lastFundingDate),
    "Funding Year": getFundingYear(c.lastFundingDate),

    "Headquarters Location": normalizeCountry(c.headquartersLocation),
    "Crunchbase Rank": rankNumber,
    Employees: c.numberOfEmployees || "Unknown",

    "Lead Type": leadType,
    "Original Lead Type": leadType,

    Reasoning: reasoning || "No justification provided",
    "Investment Stage": mapFundingStage(c.lastFundingType),
    "Estimated Revenue Range": c.estimatedRevenueRange || "",
    "Investor Type": c.investorType || "",
    "New Addition": isNewAddition,
    Version: versionNumber,
    "Updated Company": isUpdatedCompany,
    "Update Category": updateCategory,
    "Updated List Entry": isNewAddition || isUpdatedCompany,

    original: c
  };
}

function applyFeedbackOverrides(company, feedbackEntry) {
  if (!feedbackEntry?.correctedType) {
    return {
      ...company,
      "Manual Override": false,
    };
  }

  return {
    ...company,
    "Lead Type": feedbackEntry.correctedType,
    "Manual Override": feedbackEntry.correctedType !== company["Original Lead Type"],
  };
}

function getLeadTypeLabel(type) {
  if (type === "ACCEPT") return "Lead";
  if (type === "NOT ACCEPT") return "Discard";
  return "Unsure";
}

function App() {
  // -------------------------------
  // AUTH STATE
  // -------------------------------
  const [authenticated, setAuthenticated] = useState(
    localStorage.getItem("lead_auth") === "true"
  );

  // -------------------------------
  // MAIN STATE
  // -------------------------------
  const [companies, setCompanies] = useState([]);
  const [feedbackData, setFeedbackData] = useState({});
  const [loadedFeedback, setLoadedFeedback] = useState(false);

  const [currentTab, setCurrentTab] = useState("OVERVIEW");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedStage, setSelectedStage] = useState([]);
  const [selectedUpdateCategory, setSelectedUpdateCategory] = useState([]);
  const [selectedEstimatedRevenueRange, setSelectedEstimatedRevenueRange] = useState([]);
  const [selectedInvestorType, setSelectedInvestorType] = useState([]);
  const [selectedFundingPeriod, setSelectedFundingPeriod] = useState([]);
  const [selectedFundingYear, setSelectedFundingYear] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState("");
  const [selectedManualChange, setSelectedManualChange] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [rightPanelView, setRightPanelView] = useState("visualisations");
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchName, setSavedSearchName] = useState("");
  const [selectedSavedSearch, setSelectedSavedSearch] = useState("");

  const [visibleCount, setVisibleCount] = useState(20);
  const [overrideNotice, setOverrideNotice] = useState("");

  // -------------------------------------------------------
  // LOAD FEEDBACK FIRST
  // -------------------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem("lead_feedback_structured");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          setFeedbackData(parsed);
        }
      } catch (err) {
        console.error("Error parsing saved feedback:", err);
      }
    }

    const savedSearchesRaw = localStorage.getItem(SAVED_SEARCHES_KEY);
    if (savedSearchesRaw) {
      try {
        const parsed = JSON.parse(savedSearchesRaw);
        if (Array.isArray(parsed)) {
          setSavedSearches(parsed);
        }
      } catch (err) {
        console.error("Error parsing saved searches:", err);
      }
    }

    setLoadedFeedback(true);
  }, []);

  // -------------------------------------------------------
  // SAVE FEEDBACK
  // -------------------------------------------------------
  useEffect(() => {
    if (loadedFeedback) {
      localStorage.setItem(
        "lead_feedback_structured",
        JSON.stringify(feedbackData)
      );
    }
  }, [feedbackData, loadedFeedback]);

  useEffect(() => {
    if (loadedFeedback) {
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches));
    }
  }, [savedSearches, loadedFeedback]);

  // -------------------------------------------------------
  // SHARED BUTTON STYLE
  // -------------------------------------------------------
  const actionButtonStyle = {
    fontWeight: 600,
    borderWidth: 2,
    textTransform: "none",
    px: 1.75,
    py: 0.5,
    height: 34,
    fontSize: "0.82rem",
    "&:hover": { borderWidth: 2 }
  };

  // -------------------------------------------------------
  // INDUSTRY BUCKET MAP
  // -------------------------------------------------------
  // -------------------------------------------------------
  // LOAD JSON AFTER FEEDBACK
  // -------------------------------------------------------
  useEffect(() => {
    if (!loadedFeedback) return;

    Promise.all([
      fetch("/data/allDetail_pharmaron_v1.json").then((res) => res.json()),
      fetch("/data/allDetail_pharmaron_v0.json").then((res) => res.json())
    ])
      .then(([currentRaw, previousRaw]) => {
        const previousKeys = new Set(previousRaw.map(getCompanyKey));
        const normalized = currentRaw.map((c) =>
          normalizeCompany(c, !previousKeys.has(getCompanyKey(c)))
        );

        setCompanies(normalized);
      })
      .catch((err) => console.error("JSON Load Error:", err));
  }, [loadedFeedback]);

  // -------------------------------------------------------
  // LOGIN GATE
  // -------------------------------------------------------
  if (!authenticated) {
    return <Login onLogin={() => setAuthenticated(true)} />;
  }

  // -------------------------------------------------------
  // RESET FILTERS
  // -------------------------------------------------------
  const resetAll = () => {
    setSearchTerm("");
    setSelectedIndustry([]);
    setSelectedCountry([]);
    setSelectedStage([]);
    setSelectedUpdateCategory([]);
    setSelectedEstimatedRevenueRange([]);
    setSelectedInvestorType([]);
    setSelectedFundingPeriod([]);
    setSelectedFundingYear([]);
    setSelectedLeadStatus("");
    setSelectedManualChange("");
    setSortBy("");
    setSelectedSavedSearch("");
    setVisibleCount(20);
  };

  const buildSearchConfig = () => ({
    currentTab,
    searchTerm,
    selectedIndustry,
    selectedCountry,
    selectedStage,
    selectedUpdateCategory,
    selectedEstimatedRevenueRange,
    selectedInvestorType,
    selectedFundingPeriod,
    selectedFundingYear,
    selectedLeadStatus,
    selectedManualChange,
    sortBy,
  });

  const applySavedSearch = (config) => {
    setCurrentTab(config.currentTab || "OVERVIEW");
    setSearchTerm(config.searchTerm || "");
    setSelectedIndustry(normalizeSelection(config.selectedIndustry));
    setSelectedCountry(normalizeSelection(config.selectedCountry));
    setSelectedStage(normalizeSelection(config.selectedStage));
    setSelectedUpdateCategory(normalizeSelection(config.selectedUpdateCategory));
    setSelectedEstimatedRevenueRange(
      normalizeSelection(config.selectedEstimatedRevenueRange)
    );
    setSelectedInvestorType(normalizeSelection(config.selectedInvestorType));
    setSelectedFundingPeriod(normalizeSelection(config.selectedFundingPeriod));
    setSelectedFundingYear(normalizeSelection(config.selectedFundingYear));
    setSelectedLeadStatus(config.selectedLeadStatus || "");
    setSelectedManualChange(config.selectedManualChange || "");
    setSortBy(config.sortBy || "");
    setVisibleCount(20);
  };

  const saveCurrentSearch = () => {
    const trimmedName = savedSearchName.trim();
    if (!trimmedName) return;

    const nextSearch = {
      name: trimmedName,
      config: buildSearchConfig(),
    };

    setSavedSearches((prev) => {
      const withoutExisting = prev.filter((search) => search.name !== trimmedName);
      return [...withoutExisting, nextSearch].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    });
    setSelectedSavedSearch(trimmedName);
    setSavedSearchName("");
  };

  const loadSavedSearch = (name) => {
    setSelectedSavedSearch(name);
    const match = savedSearches.find((search) => search.name === name);
    if (match) {
      applySavedSearch(match.config);
    }
  };

  const deleteSavedSearch = () => {
    if (!selectedSavedSearch) return;

    setSavedSearches((prev) =>
      prev.filter((search) => search.name !== selectedSavedSearch)
    );
    setSelectedSavedSearch("");
  };

  const companiesWithOverrides = companies.map((company) =>
    applyFeedbackOverrides(company, feedbackData[company.id])
  );

  const handleClassificationChange = (companyName, nextType) => {
    setOverrideNotice(
      `${companyName} moved to ${getLeadTypeLabel(nextType)}.`
    );
  };

  const baseFilteredCompanies = companiesWithOverrides
    .filter((c) =>
      currentTab === "OVERVIEW"
        ? true
        : currentTab === "NEW_ADDITIONS"
        ? c["Updated List Entry"]
        : c["Lead Type"] === currentTab
    )
    .filter((c) =>
      c["Organization Name"]?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((c) =>
      selectedIndustry.length > 0
        ? selectedIndustry.some((industry) =>
            c.BucketIndustries.includes(industry)
          )
        : true
    )
    .filter((c) =>
      selectedCountry.length > 0
        ? selectedCountry.includes(c["Headquarters Location"])
        : true
    )
    .filter((c) =>
      selectedStage.length > 0
        ? selectedStage.includes(c["Investment Stage"])
        : true
    )
    .filter((c) =>
      selectedUpdateCategory.length > 0
        ? selectedUpdateCategory.includes(c["Update Category"])
        : true
    )
    .filter((c) =>
      selectedEstimatedRevenueRange.length > 0
        ? selectedEstimatedRevenueRange.includes(c["Estimated Revenue Range"])
        : true
    )
    .filter((c) =>
      selectedInvestorType.length > 0
        ? selectedInvestorType.includes(c["Investor Type"])
        : true
    )
    .filter((c) =>
      selectedFundingYear.length > 0
        ? selectedFundingYear.includes(c["Funding Year"])
        : true
    )
    .filter((c) =>
      currentTab === "ACCEPT" ||
      currentTab === "NOT ACCEPT" ||
      currentTab === "FUZZY"
        ? true
        : selectedLeadStatus
        ? c["Lead Type"] === selectedLeadStatus
        : true
    )
    .filter((c) =>
      selectedManualChange === "manual-only"
        ? c["Manual Override"]
        : selectedManualChange === "no-manual"
        ? !c["Manual Override"]
        : true
    );

  // -------------------------------------------------------
  // FILTERING & SORTING
  // -------------------------------------------------------
  const filtered = baseFilteredCompanies
    .filter((c) =>
      selectedFundingPeriod.length > 0
        ? selectedFundingPeriod.includes(c["Funding Quarter"]) ||
          selectedFundingPeriod.includes(c["Funding Month"])
        : true
    );

  const quarterChartCompanies =
    selectedFundingPeriod.length > 0 &&
    selectedFundingPeriod.some((period) => !QUARTER_OPTIONS.includes(period))
      ? baseFilteredCompanies
      : filtered;

  const leadStatusFilterDisabled =
    currentTab === "ACCEPT" ||
    currentTab === "NOT ACCEPT" ||
    currentTab === "FUZZY";

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "name-asc")
      return a["Organization Name"].localeCompare(b["Organization Name"]);
    if (sortBy === "name-desc")
      return b["Organization Name"].localeCompare(a["Organization Name"]);

    if (sortBy === "rank-asc")
      return (a["Crunchbase Rank"] || 999999) -
             (b["Crunchbase Rank"] || 999999);

    if (sortBy === "rank-desc")
      return (b["Crunchbase Rank"] || 0) -
             (a["Crunchbase Rank"] || 0);

    const parseEmployees = (value) => {
      if (!value) return 0;

      if (typeof value === "number") return value;

      if (value.includes("-")) {
        const [min, max] = value.split("-").map((n) => parseInt(n.trim(), 10));
        return (min + max) / 2;
      }

      return parseInt(value, 10) || 0;
    };

    if (sortBy === "employees-desc")
      return parseEmployees(b.Employees) - parseEmployees(a.Employees);

    if (sortBy === "employees-asc")
      return parseEmployees(a.Employees) - parseEmployees(b.Employees);

    return 0;
  });

  const visibleCompanies = sorted.slice(0, visibleCount);
  const hasActiveQuery =
    searchTerm.trim() !== "" ||
    selectedIndustry.length > 0 ||
    selectedCountry.length > 0 ||
    selectedStage.length > 0 ||
    selectedUpdateCategory.length > 0 ||
    selectedEstimatedRevenueRange.length > 0 ||
    selectedInvestorType.length > 0 ||
    selectedFundingPeriod.length > 0 ||
    selectedFundingYear.length > 0 ||
    selectedLeadStatus !== "" ||
    selectedManualChange !== "";
  const overviewCompanies = hasActiveQuery ? sorted : companiesWithOverrides;
  const overviewVisibleCompanies = overviewCompanies.slice(0, visibleCount);

  // -------------------------------------------------------
  // LOADING GATE
  // -------------------------------------------------------
  if (!loadedFeedback) return <div>Loading...</div>;

  // -------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------
  return (
    <>
      <Header
        onExportFeedback={(fileName) =>
          exportFeedbackToCSV(sorted, feedbackData, fileName)
        }
      />

      <Container maxWidth="xl" sx={{ paddingTop: 11, paddingBottom: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: "none" }}>
          Brokancode – Pharmaron: Lead Discovery PoC
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "380px minmax(0, 1fr)" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 380,
              justifySelf: { xs: "stretch", lg: "center" },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: 1,
                width: "100%",
                position: { xs: "static", lg: "fixed" },
                top: { lg: 84 },
                width: { lg: 380 },
                maxHeight: { lg: "calc(100vh - 88px)" },
                alignContent: { lg: "start" },
                pb: { lg: 0.5 },
                overflowY: { lg: "auto" },
                pr: { lg: 0.5 },
                scrollbarGutter: { lg: "stable" },
              }}
            >
            <Paper
              sx={{
                p: 0.5,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(e, v) => {
                  setCurrentTab(v);
                  setVisibleCount(20);
                  setRightPanelView("visualisations");
                }}
                orientation="vertical"
                variant="scrollable"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight: "auto",
                  "& .MuiTabs-flexContainer": {
                    alignItems: "stretch",
                  },
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    textAlign: "left",
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    borderRadius: 2,
                    mx: 0.5,
                    px: 1,
                    py: 0.5,
                  },
                }}
              >
                <Tab label="Updated List" value="NEW_ADDITIONS" />
                <Tab label="Overview" value="OVERVIEW" />
                <Tab label="Leads" value="ACCEPT" />
                <Tab label="Discard" value="NOT ACCEPT" />
                <Tab label="Unsure" value="FUZZY" />
              </Tabs>
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Search and Saved Views
              </Typography>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                savedSearchName={savedSearchName}
                setSavedSearchName={setSavedSearchName}
                saveCurrentSearch={saveCurrentSearch}
                savedSearches={savedSearches}
                selectedSavedSearch={selectedSavedSearch}
                loadSavedSearch={loadSavedSearch}
                deleteSavedSearch={deleteSavedSearch}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Filter Options
              </Typography>
              <Filters
                companies={companiesWithOverrides}
                selectedIndustry={selectedIndustry}
                setSelectedIndustry={setSelectedIndustry}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                selectedStage={selectedStage}
                setSelectedStage={setSelectedStage}
                selectedUpdateCategory={selectedUpdateCategory}
                setSelectedUpdateCategory={setSelectedUpdateCategory}
                selectedEstimatedRevenueRange={selectedEstimatedRevenueRange}
                setSelectedEstimatedRevenueRange={setSelectedEstimatedRevenueRange}
                selectedInvestorType={selectedInvestorType}
                setSelectedInvestorType={setSelectedInvestorType}
                selectedFundingPeriod={selectedFundingPeriod}
                setSelectedFundingPeriod={setSelectedFundingPeriod}
                selectedFundingYear={selectedFundingYear}
                setSelectedFundingYear={setSelectedFundingYear}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 0.85, fontWeight: 700, fontSize: "0.95rem" }}>
                Sort & Status
              </Typography>
              <SortBar
                sortBy={sortBy}
                setSortBy={setSortBy}
                selectedLeadStatus={selectedLeadStatus}
                setSelectedLeadStatus={setSelectedLeadStatus}
                selectedManualChange={selectedManualChange}
                setSelectedManualChange={setSelectedManualChange}
                leadStatusFilterDisabled={leadStatusFilterDisabled}
              />
            </Paper>

            <Paper
              sx={{
                p: 1.25,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 0.85,
                }}
              >
                <ResetFilters resetAll={resetAll} sx={actionButtonStyle} />

              </Box>
            </Paper>
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Paper
                sx={{
                  p: 0.35,
                  borderRadius: 999,
                  backgroundColor: "#ffffff",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Tabs
                  value={rightPanelView}
                  onChange={(e, value) => setRightPanelView(value)}
                  sx={{
                    minHeight: "auto",
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .MuiTab-root": {
                      minHeight: 34,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.5,
                      color: "#475569",
                    },
                    "& .Mui-selected": {
                      color: "#0f172a !important",
                      backgroundColor: "#e2e8f0",
                    },
                  }}
                >
                  <Tab label="Visualisations" value="visualisations" />
                  <Tab label="Company List" value="companies" />
                </Tabs>
              </Paper>
            </Box>

            {currentTab === "OVERVIEW" ? (
              <Box>
                {rightPanelView === "visualisations" ? (
                  <Overview
                    companies={overviewCompanies}
                    fundingQuarterCompanies={
                      hasActiveQuery ? quarterChartCompanies : companiesWithOverrides
                    }
                    selectedFundingPeriod={selectedFundingPeriod}
                  />
                ) : (
                  <>
                    {overviewCompanies.length === 0 ? (
                      <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                        No companies matching this search criteria
                      </Typography>
                    ) : (
                      overviewVisibleCompanies.map((c) => (
                        <CompanyCard
                          key={c.id}
                          company={c}
                          feedbackData={feedbackData}
                          setFeedbackData={setFeedbackData}
                          onClassificationChange={handleClassificationChange}
                        />
                      ))
                    )}

                    {visibleCount < overviewCompanies.length && (
                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => setVisibleCount(visibleCount + 20)}
                        >
                          Load More
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box>
                {rightPanelView === "visualisations" ? (
                  sorted.length === 0 ? (
                    <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                      No companies matching this search criteria
                    </Typography>
                  ) : (
                    <TabMiniCharts
                      companies={sorted}
                      currentTab={currentTab}
                      showClassificationSummary={currentTab === "NEW_ADDITIONS"}
                      selectedFundingPeriod={selectedFundingPeriod}
                    />
                  )
                ) : (
                  <>
                    {sorted.length === 0 ? (
                      <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                        No companies matching this search criteria
                      </Typography>
                    ) : (
                      sorted.map((c) => (
                        <CompanyCard
                          key={c.id}
                          company={c}
                          feedbackData={feedbackData}
                          setFeedbackData={setFeedbackData}
                          onClassificationChange={handleClassificationChange}
                        />
                      ))
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={Boolean(overrideNotice)}
        autoHideDuration={3000}
        onClose={() => setOverrideNotice("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOverrideNotice("")}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {overrideNotice}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;
